import { validateGuid } from "~/helpers";
import type { IParticipationCookie } from "~/types";

/**
 * Activation Token Cookie Handler Middleware
 *
 * This middleware runs first (prefixed with 00-) to handle activation tokens for Research Topics.
 * When users access RT pages via email links containing an 'activationKey' query parameter,
 * this middleware creates a cookie to store participation information.
 *
 * Cookie Details:
 * - Name: "research_topic_participation_" + RT_ID
 * - Expires: 6 months from creation
 * - Value: JSON object with activationKey and researchTopicId
 *
 */

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.client) return;

  if (!to.name?.toString().includes("research-topics-rtId")) return;

  const activationKey = to.query.activationKey;

  if (!activationKey || typeof activationKey !== "string") {
    return;
  }

  const {
    public: { isDevMode, domain }
  } = useRuntimeConfig();

  if (!validateGuid(activationKey)) {
    if (isDevMode) {
      console.warn(
        `[Activation Token Handler] Invalid GUID format for activationKey: ${activationKey}`
      );
    }
    return;
  }

  const { rtId } = to.params;
  const researchTopicId = Number(rtId);

  if (isNaN(researchTopicId) || researchTopicId <= 0) {
    if (isDevMode) {
      console.warn(
        `[Activation Token Handler] Invalid Research Topic ID: ${rtId}`
      );
    }
    return;
  }

  const cookieName = `research_topic_participation_${researchTopicId}`;
  const sixMonthsInSeconds = 180 * 24 * 60 * 60;

  const participationCookie = useCookie<IParticipationCookie>(cookieName, {
    domain: isDevMode ? undefined : `.${domain}`,
    maxAge: sixMonthsInSeconds,
    encode: (value) => JSON.stringify(value),
    decode: (value) => JSON.parse(value)
  });

  participationCookie.value = {
    activationKey,
    researchTopicId
  };
});
